import { gql, TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import { useRouter } from '@tanstack/react-router'
import { useTranslation } from '@web/src/lib/intl/react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type { DeletePlaceMutation, DeletePlaceMutationVariables } from 'types/graphql'

export const DELETE_PLACE: TypedDocumentNode<DeletePlaceMutation, DeletePlaceMutationVariables> = gql`
  mutation DeletePlace($placeIdOrSlug: String!) {
    deletePlace(placeIdOrSlug: $placeIdOrSlug) {
      id
    }
  }
`

type CustomModalProps = {
  open: boolean
  onOpenChange?: (details: { open: boolean }) => void
}

const PlaceDelete = ({
  place,
  ...props
}: CustomModalProps & {
  place: {
    id: string
    name: string
    slug?: string | null
  }
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const [showSpinner, setShowSpinnerValue] = useState(false)

  const [deletePlace] = useMutation(DELETE_PLACE, {
    update: (cache, { data }) => {
      const deletedPlaceId = data?.deletePlace?.id ?? place.id
      const cacheId = cache.identify({ __typename: 'Place', id: deletedPlaceId })

      if (cacheId) {
        cache.evict({ id: cacheId })
        cache.gc()
      }
    },
    onCompleted: () => {
      closeDialog()
      router.history.back()
      toast.success(t('Place deleted'))
    },
    onError: (error) => {
      toast.error(error.message)
      closeDialog()
    },
  })

  const onSubmit = () => {
    setShowSpinnerValue(true)
    deletePlace({
      variables: { placeIdOrSlug: place.slug ?? place.id },
    })
  }

  const closeDialog = () => {
    props?.onOpenChange?.({ open: false })
  }

  return (
    <ConfirmDialog
      confirmationPlaceholder={t('Type the place name here to confirm')}
      confirmationPrompt={t('Please enter the place name to confirm')}
      confirmationText={place.name}
      loading={showSpinner}
      onConfirm={onSubmit}
      onOpenChange={(details) => {
        props?.onOpenChange?.(details)

        if (!details.open) {
          setShowSpinnerValue(false)
        }
      }}
      open={props.open}
      text={t('Deleting this place will remove all reviews and media with it.')}
      title={t('Delete Place')}
      variant="delete"
    />
  )
}

export default PlaceDelete
